import React, { useState, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  CircularProgress,
  ButtonGroup,
  Stack,
} from "@mui/material";
import { Refresh as RefreshIcon, Search } from "@mui/icons-material";
import Loader from "@jumbo/components/Loader";
import { useDispatch, useSelector } from "react-redux";
import { usePagination } from "app/hooks/usePagination";
import { Table as CustomTable } from "app/components";
import moment from "moment";
import { useSnackbar } from "notistack";
import { LocalizationProvider, DateRangePicker } from "@mui/lab";
import AdapterDateFns from "@mui/lab/AdapterDateFns";
import { getMyntraProductReport } from "app/redux/slice/myntraSlice";

const DEFAULT_SORT_FIELD = "finalPL";
const DEFAULT_SORT_ORDER = "asc";
const DEFAULT_FILTERS = {
  search: "",
  fromDate: "",
  toDate: "",
};
const DEFAULT_GROUP_BY = "size";

const QUICK_RANGES = [
  {
    key: "today",
    label: "Today",
    getRange: () => [
      moment().startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "7d",
    label: "Last 7D",
    getRange: () => [
      moment().subtract(6, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "30d",
    label: "Last 30D",
    getRange: () => [
      moment().subtract(29, "days").startOf("day").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
  {
    key: "ytd",
    label: "YTD",
    getRange: () => [
      moment().startOf("year").toDate(),
      moment().endOf("day").toDate(),
    ],
  },
];

const buildReportParams = ({
  page,
  perPage,
  order,
  orderBy,
  filters,
  groupBy,
}) => {
  const params = {
    page,
    perPage,
    groupBy,
  };

  if (orderBy !== "") {
    params.sort = order === "asc" ? 1 : -1;
    params.field = orderBy;
  }

  if (filters?.search) {
    params.search = filters.search;
  }

  if (filters?.fromDate && filters?.toDate) {
    params.fromDate = filters.fromDate;
    params.toDate = filters.toDate;
  }

  return params;
};

const MyntraProductReport = () => {
  const dispatch = useDispatch();
  const myntraData = useSelector((state) => state?.myntra);
  const products = myntraData?.productReport?.list ?? [];
  const { enqueueSnackbar } = useSnackbar();
  const [dateRange, setDateRange] = useState([null, null]);
  const [tempSearchTerm, setTempSearchTerm] = useState("");
  const [appliedFilters, setAppliedFilters] = useState(() => ({
    ...DEFAULT_FILTERS,
  }));
  const [groupBy, setGroupBy] = useState(DEFAULT_GROUP_BY);
  const [tableResetKey, setTableResetKey] = useState(0);

  const [order, setOrder] = useState(DEFAULT_SORT_ORDER);
  const [orderBy, setOrderBy] = useState(DEFAULT_SORT_FIELD);

  const { page, rowsPerPage, handleChangePage, handleChangeRowsPerPage } =
    usePagination({ id: "myntra-product-report" });

  useEffect(() => {
    dispatch(
      getMyntraProductReport(
        buildReportParams({
          page,
          perPage: rowsPerPage,
          order,
          orderBy,
          filters: appliedFilters,
          groupBy,
        }),
      ),
    );
  }, [appliedFilters, dispatch, groupBy, order, orderBy, page, rowsPerPage]);

  const handleApplyFilter = () => {
    if ((dateRange[0] && !dateRange[1]) || (!dateRange[0] && dateRange[1])) {
      enqueueSnackbar("Please select both start and end date", {
        variant: "error",
      });
      return;
    }

    if (page !== 1) {
      handleChangePage(null, 0);
    }

    setAppliedFilters({
      search: tempSearchTerm.trim(),
      fromDate: dateRange[0] ? moment(dateRange[0]).format("YYYY-MM-DD") : "",
      toDate: dateRange[1] ? moment(dateRange[1]).format("YYYY-MM-DD") : "",
    });
  };

  const sizeColumns = [
    // { id: "productName", label: "Product Name", sort: true },
    { id: "sku", label: "SKU", sort: true },
    { id: "itemName", label: "Item Name", sort: true },
    { id: "costPerUnit", label: "Cost Per Unit", sort: true },
    {
      id: "totalOrders",
      label: "Total Orders",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "totalDelivered",
      label: "Total Delivered",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "totalReturns",
      label: "Total Returns",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "returnPercentage",
      label: "Return %",
      sort: true,
      render: (row) => <span>{(row?.returnPercentage || 0).toFixed(1)}%</span>,
    },
    {
      id: "totalCostPrice",
      label: "Total Cost",
      sort: true,
      isTotal: true,
      format: (v) => v?.toFixed(2),
    },
    {
      id: "totalPendingPayment",
      label: "Pending Payment",
      sort: true,
      isTotal: true,
      format: (v) => v?.toFixed(2),
    },
    {
      id: "totalReceivedPayment",
      label: "Received Payment",
      sort: true,
      isTotal: true,
      format: (v) => v?.toFixed(2),
    },
    {
      id: "finalPL",
      label: "Final P/L",
      sort: true,
      minWidth: 130,
      render: (row) => {
        const value = Math.abs(row?.finalPL || 0);
        const isProfit = (row?.finalPL || 0) >= 0;
        return (
          <span
            style={{
              color: isProfit ? "green" : "red",
              fontWeight: "bold",
            }}
          >
            {isProfit ? "+" : "-"}
            {value.toFixed(2)}
          </span>
        );
      },
      isTotal: true,
      format: (v) => v?.toFixed(2),
    },
  ];

  const colorColumns = [
    {
      id: "productName",
      label: "Product Name",
      sort: true,
      minWidth: 220,
      render: (row) => row?.productName || row?.marketplaceProductName || "-",
    },
    {
      id: "colorName",
      label: "Color",
      sort: true,
      minWidth: 140,
      render: (row) => row?.colorName || "-",
    },
    {
      id: "skuCount",
      label: "Total Sizes",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "costPerUnit",
      label: "Avg Cost Per Unit",
      sort: true,
      render: (row) => <span>{(row?.costPerUnit || 0).toFixed(2)}</span>,
    },
    {
      id: "totalOrders",
      label: "Total Orders",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "totalDelivered",
      label: "Total Delivered",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "totalReturns",
      label: "Total Returns",
      sort: true,
      isTotal: true,
      format: (v) => v,
    },
    {
      id: "returnPercentage",
      label: "Return %",
      sort: true,
      render: (row) => <span>{(row?.returnPercentage || 0).toFixed(1)}%</span>,
    },
    {
      id: "totalCostPrice",
      label: "Total Cost",
      sort: true,
      isTotal: true,
      format: (v) => v?.toFixed(2),
    },
    {
      id: "totalPendingPayment",
      label: "Pending Payment",
      sort: true,
      isTotal: true,
      format: (v) => v?.toFixed(2),
    },
    {
      id: "totalReceivedPayment",
      label: "Received Payment",
      sort: true,
      isTotal: true,
      format: (v) => v?.toFixed(2),
    },
    {
      id: "finalPL",
      label: "Final P/L",
      sort: true,
      minWidth: 130,
      render: (row) => {
        const value = Math.abs(row?.finalPL || 0);
        const isProfit = (row?.finalPL || 0) >= 0;
        return (
          <span
            style={{
              color: isProfit ? "green" : "red",
              fontWeight: "bold",
            }}
          >
            {isProfit ? "+" : "-"}
            {value.toFixed(2)}
          </span>
        );
      },
      isTotal: true,
      format: (v) => v?.toFixed(2),
    },
  ];

  const columns = groupBy === "color" ? colorColumns : sizeColumns;

  const sortData = (o, ob) => {
    setOrder(o);
    setOrderBy(ob);
  };

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 0 }}>
          Myntra Product Report
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant={groupBy === "size" ? "contained" : "outlined"}
            onClick={() => {
              setGroupBy("size");
              setOrderBy(DEFAULT_SORT_FIELD);
              setOrder(DEFAULT_SORT_ORDER);
              setTableResetKey((current) => current + 1);
              if (page !== 1) {
                handleChangePage(null, 0);
              }
            }}
          >
            Size Wise
          </Button>
          <Button
            variant={groupBy === "color" ? "contained" : "outlined"}
            onClick={() => {
              setGroupBy("color");
              setOrderBy(DEFAULT_SORT_FIELD);
              setOrder(DEFAULT_SORT_ORDER);
              setTableResetKey((current) => current + 1);
              if (page !== 1) {
                handleChangePage(null, 0);
              }
            }}
          >
            Color Wise
          </Button>
        </Stack>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by product, SKU..."
                value={tempSearchTerm}
                onChange={(e) => setTempSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <Search sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2.75}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateRangePicker
                  startText="From Date"
                  endText="To Date"
                  inputFormat="dd/MM/yyyy"
                  value={dateRange}
                  onChange={setDateRange}
                  renderInput={({ inputProps, ...startProps }, endProps) => {
                    const startValue = inputProps.value;
                    delete inputProps.value;
                    return (
                      <TextField
                        {...startProps}
                        inputProps={inputProps}
                        value={`${startValue || "DD/MM/YYYY"} - ${
                          endProps.inputProps.value || "DD/MM/YYYY"
                        }`}
                        fullWidth
                        size="small"
                      />
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} sm={3.25}>
              <Stack direction="row" spacing={1} alignItems="center">
                <ButtonGroup size="small" variant="outlined">
                  {QUICK_RANGES.map((r) => (
                    <Button
                      key={r.key}
                      onClick={() => setDateRange(r.getRange())}
                    >
                      {r.label}
                    </Button>
                  ))}
                </ButtonGroup>
              </Stack>
            </Grid>
            <Grid item xs={12} sm={1.5}>
              <Button
                variant="contained"
                onClick={handleApplyFilter}
                disabled={myntraData?.productReport?.loading}
                startIcon={
                  myntraData?.productReport?.loading ? (
                    <CircularProgress size={16} />
                  ) : null
                }
                fullWidth
              >
                Apply
              </Button>
            </Grid>
            <Grid item xs={12} md={1.5}>
              <Button
                variant="outlined"
                onClick={() => {
                  setTempSearchTerm("");
                  setDateRange([null, null]);
                  setAppliedFilters({ ...DEFAULT_FILTERS });
                  setGroupBy(DEFAULT_GROUP_BY);
                  setOrderBy(DEFAULT_SORT_FIELD);
                  setOrder(DEFAULT_SORT_ORDER);
                  setTableResetKey((current) => current + 1);
                  if (page !== 1) {
                    handleChangePage(null, 0);
                  }
                }}
                startIcon={<RefreshIcon />}
                fullWidth
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {myntraData?.productReport?.loading ? (
        <Loader />
      ) : (
        <Card>
          <CardContent>
            <CustomTable
              key={`${groupBy}-${tableResetKey}`}
              columns={columns}
              rows={products}
              page={page}
              rowsPerPage={rowsPerPage}
              count={myntraData?.productReport?.total || products.length}
              Order={order}
              OrderBy={orderBy}
              sortData={sortData}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
              loading={myntraData?.productReport?.loading}
              showTotalInFooter={true}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MyntraProductReport;
